import React from "react";
import styled from "styled-components";
import { styledSafe } from "src/utils/styledComponent";

export interface CommonProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;

  position?: "static" | "relative" | "absolute" | "fixed" | "sticky";
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
  zIndex?: string | number;

  width?: string;
  maxWidth?: string;
  minWidth?: string;
  height?: string;
  minHeight?: string;
  maxHeight?: string;

  scrollMarginTop?: string;
  margin?: string;
  padding?: string;
  background?: string | false;
  backgroundHovered?: string;

  borderInline?: string;
  border?: string;
  borderTop?: string;
  borderRight?: string;
  borderBottom?: string;
  borderLeft?: string;
  borderRadius?: string;
  borderWidth?: string;
  borderColor?: string;
  borderStyle?: "solid" | "dashed";

  overflow?: "auto" | "visible" | "hidden" | "scroll";
  visibility?: "visible" | "hidden";
  opacity?: number;
  transition?: string;

  flexGrow?: boolean | number;
  flexShrink?: number;

  gridColumn?: string;
  gridRow?: string;
  gridArea?: string;

  whiteSpace?: string;
  pointerEvents?: "auto" | "none";
  userSelect?: "all" | "auto" | "none" | "text";
  cursor?: "inherit" | "auto" | "pointer" | "default" | "grab";
  draggable?: boolean;

  ratio?: string;
  rotate?: number;
  boxShadow?: string;
  color?: string;

  isDisabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onPointerDown?: React.PointerEventHandler<HTMLDivElement>;
  onPointerEnter?: React.PointerEventHandler<HTMLDivElement>;
  onPointerLeave?: React.PointerEventHandler<HTMLDivElement>;
  onContextMenu?: React.MouseEventHandler<HTMLDivElement>;
  children?: React.ReactNode;
}

export interface BoxProps extends CommonProps {
  display?: "none" | "block" | "inline";
}

export const BoxElement = styledSafe("div")<BoxProps>`
  display: ${({ display }) => display};

  position: ${({ position }) => position};
  top: ${({ top }) => top};
  right: ${({ right }) => right};
  bottom: ${({ bottom }) => bottom};
  left: ${({ left }) => left};
  z-index: ${({ zIndex }) => zIndex};

  width: ${({ width }) => width};
  max-width: ${({ maxWidth }) => maxWidth};
  min-width: ${({ minWidth }) => minWidth};
  height: ${({ height }) => height};
  min-height: ${({ minHeight, flexGrow }) =>
    minHeight || (flexGrow !== undefined && flexGrow !== false && "0")};
  max-height: ${({ maxHeight }) => maxHeight};

  margin: ${({ margin }) => margin};
  padding: ${({ padding }) => padding};
  scroll-margin-top: ${({ scrollMarginTop }) => scrollMarginTop};

  background: ${({ background }) => background !== false && background};
  ${({ backgroundHovered }) =>
    backgroundHovered && `&:hover { background: ${backgroundHovered}; }`};

  border-inline: ${({ borderInline, borderColor }) =>
    borderInline || (borderColor && `1px solid ${borderColor}`)};
  border: ${({ border, borderColor }) => border || (borderColor && `1px solid ${borderColor}`)};
  border-top: ${({ borderTop }) => borderTop};
  border-right: ${({ borderRight }) => borderRight};
  border-bottom: ${({ borderBottom }) => borderBottom};
  border-left: ${({ borderLeft }) => borderLeft};
  border-radius: ${({ borderRadius }) => borderRadius};
  border-width: ${({ borderWidth }) => borderWidth};
  border-color: ${({ borderColor }) => borderColor};
  border-style: ${({ borderStyle }) => borderStyle};

  overflow: ${({ overflow }) => overflow};
  visibility: ${({ visibility }) => visibility};
  opacity: ${({ opacity }) => opacity};
  transition: ${({ transition }) => transition};

  flex-grow: ${({ flexGrow }) =>
    (flexGrow === true && "1") ||
    ((flexGrow === false || flexGrow === undefined) && undefined) ||
    (flexGrow as number)};
  flex-shrink: ${({ flexShrink }) => flexShrink};

  grid-column: ${({ gridColumn }) => gridColumn};
  grid-row: ${({ gridRow }) => gridRow};
  grid-area: ${({ gridArea }) => gridArea};

  white-space: ${({ whiteSpace }) => whiteSpace};
  pointer-events: ${({ pointerEvents }) => pointerEvents};
  user-select: ${({ userSelect, isDisabled, onClick }) =>
    userSelect || ((isDisabled || onClick) && "none")};
  cursor: ${({ cursor, isDisabled, onClick }) =>
    cursor || (isDisabled && "default") || (onClick && "pointer")};
  color: ${({ color }) => color};

  transform: ${({ rotate }) => rotate && `rotate(${rotate}deg)`};
  box-shadow: ${({ boxShadow }) => boxShadow};
  aspect-ratio: ${({ ratio }) => ratio};
`;

export const Box = React.forwardRef<HTMLDivElement, BoxProps>((props, ref) => (
  <BoxElement ref={ref} {...props} {...(props.isDisabled && { onClick: undefined })}>
    {props.children}
  </BoxElement>
));

export interface FlexProps extends CommonProps {
  display?: "flex" | "inline-flex";
  direction?: "row" | "column";
  column?: boolean;
  flexWrap?: "nowrap" | "wrap" | "wrap-reverse";
  justify?:
    | "normal"
    | "center"
    | "left"
    | "right"
    | "baseline"
    | "evenly"
    | "between"
    | "around"
    | "space-evenly"
    | "space-between"
    | "space-around"
    | "stretch";
  align?:
    | "normal"
    | "start"
    | "top"
    | "end"
    | "bottom"
    | "center"
    | "baseline"
    | "stretch"
    | "between"
    | "around"
    | "space-between"
    | "space-around";
  alignContent?: "baseline";
  gap?: string;
}

const convertJustify = (justify?: FlexProps["justify"]) => {
  return (
    (justify === "evenly" && "space-evenly") ||
    (justify === "around" && "space-around") ||
    (justify === "between" && "space-between") ||
    justify
  );
};

const convertAlign = (align?: FlexProps["align"]) => {
  return (
    (align === "top" && "start") ||
    (align === "bottom" && "end") ||
    (align === "around" && "space-around") ||
    (align === "between" && "space-between") ||
    align
  );
};

export const Flex = styled(Box)<FlexProps>`
  display: ${({ display }) => display || "flex"};
  flex-direction: ${({ column, direction }) => (column && "column") || direction};
  flex-wrap: ${({ flexWrap }) => flexWrap};
  justify-content: ${({ column, direction, justify, align }) =>
    !column && direction !== "column" ? convertJustify(justify) : convertAlign(align)};
  align-items: ${({ column, direction, justify, align }) =>
    !column && direction !== "column" ? convertAlign(align) : convertJustify(justify)};
  align-content: ${({ alignContent }) => alignContent};
  gap: ${({ gap }) => gap};
`;

export interface GridProps extends CommonProps {
  display?: "grid";
  rows?: string;
  columns?: string;
  areas?: string;
  gap?: string;
  justify?: FlexProps["justify"];
  align?: FlexProps["align"];
  alignContent?: FlexProps["alignContent"];
}

export const Grid = styled(Box)<GridProps>`
  display: grid;
  grid-template-rows: ${({ rows }) => rows};
  grid-template-columns: ${({ columns }) => columns};
  grid-template-areas: ${({ areas }) => areas};
  gap: ${({ gap }) => gap};
  justify-content: ${({ justify }) => convertJustify(justify)};
  align-items: ${({ align }) => convertAlign(align)};
  align-content: ${({ alignContent }) => alignContent};
`;

export const SubGrid = styled(Grid)`
  grid-template-columns: subgrid;
`;
